import { Typography } from '@components/ui';
import useIsCompact from '@hooks/app/useIsCompact';
import usePendingActionsBase from '@hooks/query/pendingActions/usePendingActionsBase';
import useLastMessages from '@hooks/xmpp/useLastMessages';
import {
  ArrowLeftOutlined,
  BusinessAccount,
  CalendarOutlined,
  Chat,
  Dashboard,
  Erecepty,
  EvisitOutlined,
  Logo,
  LogoSymbol,
  Oznamy,
  PatientsInactive,
  Tags,
} from '@icon';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useMediaQuery } from 'react-responsive';
import { useTheme } from 'styled-components';
import DestinationLink from './components/destination-link';
import { SidebarHandle, SidebarSC } from './sidebar.styled';

type TSiderbarProps = {};

const Sidebar: React.FC<TSiderbarProps> = ({}) => {
  const theme = useTheme();
  const { data: pendingActions } = usePendingActionsBase({ refetchInterval: 60000 });
  const { unreadCount } = useLastMessages();
  const isCompact = useIsCompact();

  const [collapsed, setCollapsed] = React.useState(isCompact ? true : false);

  const isSmall = useMediaQuery({ query: '(max-width: 1400px)' });

  const handleToggle = React.useCallback((isCollapsed?: boolean) => {
    setCollapsed((o) => {
      const next = isCollapsed !== undefined ? isCollapsed : !o;

      next
        ? document.body.classList.add('sidebar-collapsed')
        : document.body.classList.remove('sidebar-collapsed');
      return next;
    });
  }, []);

  React.useEffect(() => {
    if (isSmall || isCompact) {
      handleToggle(true);
    } else {
      handleToggle(false);
    }
  }, [isSmall, isCompact]);

  return (
    <SidebarSC>
      {isCompact ? null : (
        <SidebarHandle onClick={() => handleToggle()}>
          <div className="handle-inner">
            <div className="handle">
              <ArrowLeftOutlined color="white" width={20} height={20} />
            </div>
            <Typography display="buttonText" className="close-text" as="div" noWrap>
              <FormattedMessage id={collapsed ? 'sidebar.openSidebar' : 'sidebar.closeSidebar'} />
            </Typography>
          </div>
        </SidebarHandle>
      )}
      <nav className="side-nav-bar">
        <div className="inner">
          <DestinationLink Icon={Dashboard} to="/" exact badge={pendingActions?.overiewCount}>
            <FormattedMessage id="sidebar.dashboard" />
          </DestinationLink>
          <DestinationLink Icon={PatientsInactive} to="/patients">
            <FormattedMessage id="sidebar.patients" />
          </DestinationLink>
          <DestinationLink Icon={Chat} to="/communication" badge={unreadCount}>
            <FormattedMessage id="sidebar.communication" />
          </DestinationLink>
          <DestinationLink Icon={Oznamy} to="/alerts">
            <FormattedMessage id="sidebar.notifications" />
          </DestinationLink>
          <DestinationLink Icon={Erecepty} to="/receipts" badge={pendingActions?.receiptCount}>
            <FormattedMessage id="sidebar.receipts" />
          </DestinationLink>
          <DestinationLink
            Icon={BusinessAccount}
            to="/business"
            badge={pendingActions?.bussinesCount}
          >
            <FormattedMessage id="sidebar.business-profile" />
          </DestinationLink>
          <DestinationLink Icon={EvisitOutlined} to="/e-visits/calendar">
            <FormattedMessage id="sidebar.e-visit" />
          </DestinationLink>
          <DestinationLink Icon={CalendarOutlined} to="/office-hours">
            <FormattedMessage id="office_hours_title" />
          </DestinationLink>
          <DestinationLink Icon={Tags} to="/tags">
            <FormattedMessage id="sidebar.tags" />
          </DestinationLink>
        </div>
      </nav>
      <div className="sidebar-bottom-logo">
        {collapsed ? (
          <LogoSymbol width={48} height={48} color={theme.color.surface.secondaryDark} />
        ) : (
          <Logo height={48} width={132} color={theme.color.surface.secondaryDark} />
        )}
      </div>
    </SidebarSC>
  );
};

export default React.memo(Sidebar);
